import { ErrorBoundary } from '@sentry/react'
import React, { Suspense } from 'react'
import { LuX } from 'react-icons/lu'

import BackLink from '@/components/misc/BackLink'
import CompanyProfile from '@/components/misc/CompanyProfile/CompanyProfile'
import CompanyProfileSkeleton from '@/components/misc/CompanyProfile/components/CompanyProfileSkeleton'
import FirmDetailsNotFound from '@/components/misc/FirmDetailsNotFound'
import FullPageErrorFallback from '@/components/misc/FullPageErrorFallback'
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
} from '@/components/ui/drawer'
import { useBackLinkParams } from '@/hooks/useBackLinkParams'
import { useCompanyProfileSearchParams } from '@/hooks/useCompanyProfileSearchParams'
import { useIsRoute } from '@/hooks/useIsRoute'
import { ROUTES } from '@/lib/routes'
import errorTypeService from '@/lib/services/errorTypeService'
import { CompanyProfileIdentifierType } from '@/types/misc'

const CompanyProfileModal: React.FC = () => {
  const { clearParams, searchParams } = useCompanyProfileSearchParams()
  const { backUrl, backLabel } = useBackLinkParams()
  const { identifier, identifierType } = searchParams
  const isRoute = useIsRoute()

  if (!identifier || !identifierType) {
    return null
  }

  if (isRoute(ROUTES._companyProfile)) {
    return null
  }

  const shouldShowBackLink =
    backLabel && backUrl && !isRoute(ROUTES._referralSummary)

  return (
    <>
      <Drawer open direction="right" onHide={clearParams}>
        <DrawerContent>
          <DrawerHeader className="flex flex-row justify-between gap-2 px-4">
            {shouldShowBackLink ? (
              <BackLink label={backLabel} to={backUrl} className="mb-0" />
            ) : (
              <div />
            )}

            <DrawerClose asChild>
              <LuX className="text-xl" />
            </DrawerClose>
          </DrawerHeader>

          <DrawerBody>
            <ErrorBoundary
              fallback={(error) =>
                errorTypeService.is404Error(error.error) ? (
                  <FirmDetailsNotFound className="px-6" />
                ) : (
                  <FullPageErrorFallback error={error} className="mt-0" />
                )
              }
            >
              <Suspense fallback={<CompanyProfileSkeleton />}>
                <CompanyProfile
                  identifier={identifier}
                  identifierType={
                    identifierType as CompanyProfileIdentifierType
                  }
                />
              </Suspense>
            </ErrorBoundary>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  )
}

export default CompanyProfileModal
